import {
  Edit,
  Eye,
  PlusCircle,
  Trash2,
  RotateCcw,
  ChevronUp,
} from "feather-icons-react/build/IconComponents";
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Table from "../../core/pagination/datatable";
import ProductDetailsView from "./productDetailsView";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { setToogleHeader } from "../../core/redux/action";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import {
  fetchProductVariationsWithInventory,
  deleteProduct,
  deleteProductVariation,
} from "../../core/api/products";
import Swal from "sweetalert2";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { secureExportToExcel } from "../../utils/secureExcelUtils";
// import { getCurrentUser } from "../../core/api/settings";
// import { getProfile } from "../../core/api/auth.js";
import { message } from "antd";

const ProductList = forwardRef(({ addDynamicTab }, ref) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);

  const selectedClients = useSelector((state) => state.selectedClients);
  const [products, setProducts] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5000);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const gridApiRef = useRef(null);
  // Track pending request key to avoid duplicate calls
  const pendingRequestRef = useRef(null);

  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [productId, setProductId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const sidebarRef = useRef(null);
  const [currentVariationId, setCurrentVariationId] = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);

  const toggleSidebarEditMode = () => {
    setIsEditMode((prevMode) => !prevMode);
  };
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Expose fetchProducts method to parent component
  useImperativeHandle(ref, () => ({
    fetchProducts: (page, pageSize) => fetchProducts(page || currentPage, pageSize || 5000),
  }));

  const handleCloseSidebar = () => {
    setSidebarVisible(false);
    setProductId(null);
    setIsEditMode(false);
    setCurrentVariationId(null);
    setWarehouseId(null);
    fetchProducts(currentPage, pageSize);
  };

  // This function will be called only when needed (page change, refresh, etc.)
  const fetchProducts = async (page = 1, pageSize = 1000) => {
    // Build unique request key
    const requestKey = `${selectedClients.join(",")}-${page}-${pageSize}`;
    // Skip if identical request already in-flight
    if (pendingRequestRef.current === requestKey) {
      console.log("Skipping duplicate request", requestKey);
      return;
    }
    // Mark this request as pending
    pendingRequestRef.current = requestKey;

    // Ensure page and pageSize are valid numbers
    const validPage = typeof page === "number" && !isNaN(page) ? page : 1;
    const validPageSize = typeof pageSize === "number" && !isNaN(pageSize) ? pageSize : 5000;

    setLoading(true);
    // Show loading overlay in the grid if it's available
    if (gridApiRef.current) {
      gridApiRef.current.showLoadingOverlay();
    }

    // Log the start time to measure performance
    const startTime = performance.now();

    try {
      // Make sure we're not sending duplicate requests for the same page
      const response = await fetchProductVariationsWithInventory(
        selectedClients,
        validPage,
        validPageSize
      );

      console.log("product responses: ", response);

      // Log the API response time
      const apiResponseTime = performance.now() - startTime;
      console.log(`Products API response time: ${apiResponseTime.toFixed(2)}ms`);

      // Update state with new data
      setProducts(response.data.results);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.page);
      setPageSize(response.data.page_size);
      setTotalPages(response.data.total_pages);
      setDataLoaded(true);

      // Update the grid with new data if it exists
      if (gridApiRef.current) {
        const api = gridApiRef.current;

        // Make sure we're on the right page
        if (api.paginationProxy) {
          const currentPage = api.paginationProxy.getCurrentPage() + 1;
          if (currentPage !== validPage) {
            api.paginationGoToPage(validPage - 1);
          }
        }

        // Update the page size if needed
        if (api.paginationProxy && api.paginationProxy.getPageSize() !== validPageSize) {
          api.paginationProxy.setPageSize(validPageSize);
        }
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      // Show no rows overlay if there's an error
      if (gridApiRef.current) {
        gridApiRef.current.showNoRowsOverlay();
      }
      message.error("Failed to load products. Please try again.");
    } finally {
      // Log total operation time including state updates
      const totalTime = performance.now() - startTime;
      console.log(`Total products data loading time: ${totalTime.toFixed(2)}ms`);

      // Clear pending request flag
      if (pendingRequestRef.current === requestKey) {
        pendingRequestRef.current = null;
      }
      setLoading(false);
      // Hide loading overlay
      if (gridApiRef.current) {
        gridApiRef.current.hideOverlay();
      }
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  useEffect(() => {
    // Only fetch data when selectedClients changes and prevent duplicate initial loads
    if (selectedClients && selectedClients.length > 0 && !dataLoaded) {
      fetchProducts(1, pageSize);
    }
  }, [selectedClients]);

  useEffect(() => {
    const updateSidebarWidth = () => {
      if (sidebarRef.current) {
        setSidebarWidth(sidebarRef.current.getBoundingClientRect().width);
      }
    };

    if (isSidebarVisible) {
      updateSidebarWidth();
      window.addEventListener("resize", updateSidebarWidth);
    }

    return () => {
      window.removeEventListener("resize", updateSidebarWidth);
    };
  }, [isSidebarVisible]);

  const handleDeleteProduct = async (productId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteProduct(productId);
          Swal.fire("Deleted!", "The product has been deleted.", "success");
          fetchProducts(currentPage, pageSize);
        } catch (error) {
          Swal.fire("Error!", "Failed to delete the product.", "error");
        }
      }
    });
  };

  const handleDeleteProductVariation = (variationId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteProductVariation(variationId);
          Swal.fire("Deleted!", "The product variation has been deleted.", "success");
        } catch (error) {
          Swal.fire("Error!", "Failed to delete the product variation.", "error");
        }
      }
    });
  };

  const handleAddNewProduct = async () => {
    if (!selectedClients) {
      message.warning("Please select a client.");
      return;
    }
    if (selectedClients.length === 0) {
      message.warning("Please select a client.");
      return;
    }

    if (selectedClients.length > 1) {
      message.warning("Please select one client.");
      return;
    }

    if (selectedClients[0] === "all") {
      message.warning("Please select one client.");
      return;
    }

    const clientId = selectedClients[0];

    // Use the addDynamicTab which will handle URL update
    addDynamicTab("add-product", {
      clientId,
      title: "Add Product",
    });

    // Prevent default form submission
    return false;
  };

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const [currentTableData, setCurrentTableData] = useState([]);

  const handleTableChange = (pagination, filters, sorter, extra) => {
    if (extra.currentDataSource) {
      setCurrentTableData(extra.currentDataSource);
    }
  };

  const exportToExcel = async () => {
    const exportColumns = [
      { title: "Brand", dataIndex: "brand" },
      { title: "Product", dataIndex: "name" },
      { title: "Product Code", dataIndex: "product_code" },
      { title: "Product Type", dataIndex: "product_type__name" },
      { title: "Product Universe", dataIndex: "product_universe__name" },
      { title: "Product Variation Code", dataIndex: "variation_code" },
      { title: "Warehouse", dataIndex: "warehouse_name" },
      { title: "Inventory Date", dataIndex: "inventory_date" },
      { title: "Inventory on Hand", dataIndex: "inventory_on_hand" },
      { title: "Value per Unit", dataIndex: "value_per_unit" },
      {
        title: "Inventory on Hand Value",
        calculated: true,
        calculate: (product) =>
          product.inventory_on_hand && product.value_per_unit
            ? `$${(product.inventory_on_hand * product.value_per_unit).toFixed(2)}`
            : "$0.00",
      },
      {
        title: "Total Predicted Inventory on Hand",
        dataIndex: "total_predicted_inventory_on_hand",
        calculated: true,
        calculate: (product) => product.in_progress_delivery + product.inventory_on_hand,
      },
      {
        title: "Total Predicted Inventory Value",
        calculated: true,
        calculate: (product) =>
          "$" +
          (product.in_progress_delivery + product.inventory_on_hand || 0) * product.value_per_unit,
      },
    ];

    const exportData = currentTableData.map((product) => {
      const row = {};
      exportColumns.forEach((col) => {
        if (col.calculated) {
          row[col.title] = col.calculate(product);
        } else if (col.dataIndex === "value_per_unit") {
          row[col.title] = product[col.dataIndex]
            ? `$${product[col.dataIndex].toFixed(2)}`
            : "$0.00";
        } else if (col.dataIndex === "inventory_date") {
          row[col.title] = product[col.dataIndex] || "N/A";
        } else {
          row[col.title] = product[col.dataIndex] || "";
        }
      });
      return row;
    });

    secureExportToExcel(exportData, "product_list.xlsx");
  };

  const columnDefs = [
    {
      headerName: "#",
      valueGetter: (params) => {
        return (currentPage - 1) * pageSize + params.node.rowIndex + 1;
      },
      width: 70,
      minWidth: 70,
      maxWidth: 80,
      sortable: false,
      filter: false,
    },
    {
      headerName: "Brand",
      field: "brand__brand_name",
      width: 160,
      minWidth: 140,
    },
    {
      headerName: "Status",
      field: "status_name",
      width: 130,
      minWidth: 120,
      cellRenderer: (params) => {
        const value = params.value || "";
        const badgeClass =
          value === "Confirmed"
            ? "badge bg-success"
            : value === "Pending"
            ? "badge bg-warning"
            : "badge bg-danger";
        return <span className={badgeClass}>{value}</span>;
      },
    },
    {
      headerName: "Product",
      field: "name",
      width: 200,
      minWidth: 180,
      flex: 1.5,
      cellRenderer: (params) => {
        const record = params.data;
        return (
          <span
            className="clickable-link"
            onClick={() => {
              if (addDynamicTab) {
                addDynamicTab("view-product", {
                  title: `View Product (${record.id})`,
                  clientId: record.brand__client__id,
                  productId: record.id,
                  variationId: record.variation_id,
                  warehouseId: record.warehouse_id,
                });
              }
            }}>
            <u>{params.value}</u>
          </span>
        );
      },
    },
    {
      headerName: "Product Code",
      field: "product_code",
      width: 150,
      minWidth: 130,
    },
    {
      headerName: "Product Type",
      field: "product_type__name",
      width: 150,
      minWidth: 130,
    },
    {
      headerName: "Product Universe",
      field: "product_universe__name",
      width: 160,
      minWidth: 140,
    },
    {
      headerName: "Variation Code",
      field: "variation_code",
      width: 200,
      minWidth: 180,
      flex: 1,
      cellRenderer: (params) => {
        const record = params.data;
        return (
          <span
            className="clickable-link"
            onClick={() => {
              if (addDynamicTab) {
                addDynamicTab("view-product", {
                  title: `View Product (${record.id})`,
                  clientId: record.brand__client__id,
                  productId: record.id,
                  variationId: record.variation_id,
                  warehouseId: record.warehouse_id,
                });
              }
            }}>
            <u>{params.value}</u>
          </span>
        );
      },
    },
    {
      headerName: "Logistic Codification",
      field: "logistic_codification",
      width: 200,
      minWidth: 180,
      flex: 1,
    },
    {
      headerName: "Warehouse",
      field: "warehouse_name",
      width: 160,
      minWidth: 140,
    },
    {
      headerName: "Inventory Date",
      field: "inventory_date",
      width: 150,
      minWidth: 140,
    },
    {
      headerName: "Inventory on Hand",
      field: "inventory_on_hand",
      width: 160,
      minWidth: 150,
      valueFormatter: (params) => params.value ?? 0,
    },
    {
      headerName: "Value per Unit",
      field: "value_per_unit",
      width: 150,
      minWidth: 130,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(3)}` : "$0.000"),
    },
    {
      headerName: "Inventory Value",
      field: "inventory_on_hand_value",
      width: 160,
      minWidth: 150,
      valueGetter: (params) => {
        const { inventory_on_hand, value_per_unit } = params.data;
        return inventory_on_hand && value_per_unit
          ? `$${(inventory_on_hand * value_per_unit).toFixed(3)}`
          : "$0.000";
      },
    },
    {
      headerName: "Predicted On Hand",
      field: "total_predicted_inventory_on_hand",
      width: 180,
      minWidth: 160,
      valueFormatter: (params) => params.value ?? 0,
    },
    {
      headerName: "Predicted Value",
      field: "total_predicted_inventory_value",
      width: 180,
      minWidth: 160,
      valueGetter: (params) => {
        const { total_predicted_inventory_on_hand, value_per_unit } = params.data;
        return total_predicted_inventory_on_hand && value_per_unit
          ? `$${(total_predicted_inventory_on_hand * value_per_unit).toFixed(3)}`
          : "$0.000";
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      pinned: "right",
      width: 160,
      minWidth: 150,
      maxWidth: 180,
      filter: false,
      cellRenderer: (params) => {
        const record = params.data;
        return (
          <div className="action-table-data">
            <div className="edit-delete-action d-flex align-items-center">
              <a
                className="me-2 p-2"
                title="View"
                onClick={() => {
                  if (addDynamicTab) {
                    addDynamicTab("view-product", {
                      title: `View: ${record.name || record.id}`,
                      clientId: record.brand__client__id,
                      productId: record.id,
                      variationId: record.variation_id,
                      warehouseId: record.warehouse_id,
                    });
                  }
                }}>
                <Eye className="feather-view" style={{ width: 16, height: 16 }} />
              </a>

              <a
                className="me-2 p-2"
                title="Edit"
                onClick={async () => {
                  if (addDynamicTab) {
                    addDynamicTab("edit-product", {
                      title: `Edit: ${record.name || record.id}`,
                      clientId: record.brand__client__id,
                      productId: record.id,
                      variationId: record.variation_id,
                      warehouseId: record.warehouse_id,
                    });
                  }
                }}>
                <Edit className="feather-edit" style={{ width: 16, height: 16 }} />
              </a>

              <a
                className="p-2 confirm-text"
                title="Delete"
                onClick={() =>
                  record.variation_id
                    ? handleDeleteProductVariation(record.variation_id)
                    : handleDeleteProduct(record.id)
                }>
                <Trash2 className="feather-delete" style={{ width: 16, height: 16 }} />
              </a>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div
        className="p-1"
        style={{
          width: isSidebarVisible ? `calc(100% - ${sidebarWidth}px)` : "100%",
          transition: "width 0.3s ease-in-out",
        }}>
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Product List</h4>
            </div>
          </div>

          <ul className="table-top-head">
            <div className="search-set">
              <div className="search-input">
                <input
                  type="text"
                  placeholder="Search"
                  className="form-control form-control-sm formsearch"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <Link to="#" className="btn btn-searchset">
                  <i data-feather="search" className="feather-search" />
                </Link>
              </div>
            </div>
            <li>
              <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                <Link data-bs-toggle="tooltip" data-bs-placement="top" onClick={exportToExcel}>
                  <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  onClick={() => {
                    fetchProducts(currentPage, pageSize);
                  }}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch(setToogleHeader(!data));
                  }}>
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>

          <div className="page-btn">
            <button
              className="btn btn-added"
              onClick={() => {
                handleAddNewProduct();
              }}>
              <PlusCircle className="me-2 iconsize" />
              Add New Product
            </button>
          </div>
        </div>
        <div className="">
          <div className="">
            <div className="table-responsive">
              {/* No pagination at the top */}
              <div
                className="ag-theme-quartz position-relative"
                style={{ height: "700px", width: "100%" }}>
                {/* Loading Overlay */}
                {loading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      zIndex: 1000,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}>
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div style={{ marginTop: "15px", fontWeight: "bold" }}>Loading products...</div>
                  </div>
                )}
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={products}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: true,
                    minWidth: 100,
                    wrapText: true,
                  }}
                  pagination={true}
                  paginationPageSize={pageSize}
                  paginationPageSizeSelector={[10, 25, 50, 100, 500, 1000, 2000, 5000]}
                  domLayout="normal"
                  rowModelType="clientSide"
                  suppressPaginationPanel={true}
                  paginationAutoPageSize={false}
                  onFirstDataRendered={(params) => {
                    params.api.sizeColumnsToFit();
                    if (params.api.paginationProxy) {
                      params.api.setPagination(true);
                      params.api.paginationProxy.setPageSize(pageSize);
                    }
                  }}
                  onGridReady={(params) => {
                    gridApiRef.current = params.api;

                    // If we're currently loading data, show the loading overlay
                    if (loading) {
                      params.api.showLoadingOverlay();
                    }

                    // Only fetch data on grid ready if we haven't loaded it yet
                    if (!dataLoaded && selectedClients && selectedClients.length > 0) {
                      fetchProducts(currentPage, pageSize);
                    } else {
                      setTimeout(() => {
                        params.api.paginationGoToPage(currentPage - 1);

                        const api = params.api;
                        if (api.paginationGetTotalPages) {
                          const paginationPanel = document.querySelector(".ag-paging-panel");
                          if (paginationPanel) {
                            const rowSummary = paginationPanel.querySelector(
                              ".ag-paging-row-summary-panel"
                            );
                            if (rowSummary) {
                              const text = rowSummary.textContent;
                              if (text && !text.includes(totalRows.toString())) {
                                if (api.paginationProxy) {
                                  api.paginationProxy.setPageSize(pageSize);
                                }
                              }
                            }
                          }
                        }
                      }, 100);
                    }
                  }}
                  onPaginationChanged={(params) => {
                    if (!params.api || loading) return;

                    if (params.api.paginationProxy) {
                      const newPage = params.api.paginationProxy.getCurrentPage() + 1;
                      const newPageSize = params.api.paginationProxy.getPageSize();

                      if (newPage !== currentPage || newPageSize !== pageSize) {
                        setCurrentPage(newPage);
                        setPageSize(newPageSize);
                        fetchProducts(newPage, newPageSize);
                      }
                    }
                  }}
                  onRowClicked={(e) => {
                    const record = e.data;
                    setProductId(record.id);
                    setClientId(record.brand__client__id);
                    setCurrentVariationId(record.variation_id);
                    setWarehouseId(record.warehouse_id);
                  }}
                />

                <div
                  className="custom-pagination-footer"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 20px",
                    borderTop: "1px solid #ddd",
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "white",
                    zIndex: 1000,
                    boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
                  }}>
                  <div className="pagination-info">
                    Showing {products.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
                    {Math.min(currentPage * pageSize, totalRows)} of {totalRows} entries
                  </div>
                  <div
                    className="pagination-controls"
                    style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <div className="page-size-selector">
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value);
                          setPageSize(newSize);
                          fetchProducts(1, newSize); // Reset to first page with new size
                        }}
                        style={{ padding: "5px", border: "1px solid #ddd", borderRadius: "4px" }}>
                        {[10, 25, 50, 100, 500, 1000, 2000, 5000].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="page-navigation" style={{ display: "flex", gap: "5px" }}>
                      <button
                        onClick={() => fetchProducts(1, pageSize)}
                        disabled={currentPage === 1}
                        style={{
                          padding: "5px 10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === 1 ? 0.5 : 1,
                        }}>
                        First
                      </button>
                      <button
                        onClick={() => fetchProducts(currentPage - 1, pageSize)}
                        disabled={currentPage === 1}
                        style={{
                          padding: "5px 10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === 1 ? 0.5 : 1,
                        }}>
                        Previous
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        // Show pages around current page
                        let pageNum;
                        if (totalPages <= 5) {
                          // If 5 or fewer pages, show all
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          // If near start, show first 5
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          // If near end, show last 5
                          pageNum = totalPages - 4 + i;
                        } else {
                          // Otherwise show 2 before and 2 after current
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => fetchProducts(pageNum, pageSize)}
                            style={{
                              padding: "5px 10px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              backgroundColor: currentPage === pageNum ? "#007bff" : "white",
                              color: currentPage === pageNum ? "white" : "black",
                            }}>
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => fetchProducts(currentPage + 1, pageSize)}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: "5px 10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages ? 0.5 : 1,
                        }}>
                        Next
                      </button>
                      <button
                        onClick={() => fetchProducts(totalPages, pageSize)}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: "5px 10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages ? 0.5 : 1,
                        }}>
                        Last
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ProductList.displayName = "ProductList";

export default ProductList;
